/**
 * Encryption identity repair.
 *
 * Some accounts hold a vault keypair that differs from the public key the
 * server publishes for them (before 2026-09-08 the server silently kept the
 * first key it saw). Nothing sealed to the published key can be opened, so
 * sharing fails in both directions. The only secret the user still has is the
 * vault's, so the repair publishes that key — confirmed with the password, so
 * a stolen session can't swap an identity. Owners of the user's shared maps
 * are notified and restore access map by map (MapKeyTrust.restoreMemberAccess).
 *
 * Also the key decisions around it (security review 2026-09-24): restoring a
 * member's access, trusting a collaborator's changed key, and joining a map,
 * each with the fingerprints shown and, where a key changed, an explicit
 * out-of-band verification instead of a plain OK (A2, S-L1, S-L5).
 */

import { escapeHtml } from "../../../engine/core/validation.js";
import { keypairManager } from "./share-modal.js";
import { Keypair } from "../../../engine/crypto/keypair.js";
import { passwordProof } from "../../../engine/auth/handlers.js";
import * as MapKeyTrust from "../../../engine/crypto/map-key-trust.js";
import { getConnectionState } from "../../../engine/sync/user-crdt-bridge.js";
import { t } from "../../../shared/i18n/index.js";

const DISMISS_KEY = "orbital_identity_repair_later";
const headers = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" };

/** @returns {{local: string, published: string}|null} */
export function getIdentityMismatch() {
  return keypairManager?.identityMismatch || null;
}

/**
 * Publish this vault's public key for the account.
 * @param {string} password - proves the account (sent only as the login key)
 * @returns {Promise<{restoresRequested: number, restored: number, pending: number}>}
 */
export async function repairIdentity(password) {
  // Publish only the key of a vault that is in sync with the server, re-read
  // right now: the mismatch shown may come from this device's older copy of
  // the vault (read at sign-in, before the sync), and publishing that key —
  // then writing it back to the vault — would replace the account's key on
  // every device.
  if (getConnectionState() !== "synced") {
    throw new Error(t("account.identity.syncing"));
  }
  await keypairManager.initialize();
  const mismatch = getIdentityMismatch();
  if (!mismatch) return { alreadyMatches: true, restoresRequested: 0, restored: 0, pending: 0 };
  const proof = await passwordProof(password);
  const response = await fetch("/api/keypair/repair", {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ public_key: mismatch.local, ...proof }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || t("account.identity.repairFailed"));
  keypairManager.identityMismatch = null;
  await keypairManager.markSynced();
  // Maps whose owners already restored access (rare right away) become readable.
  const { restored, pending } = await MapKeyTrust.refreshRestoredShares();
  return { restoresRequested: body.restores_requested || 0, restored: restored.length, pending: pending.length };
}

/** Show the repair dialog once per session while the mismatch persists. */
export function maybePromptIdentityRepair() {
  if (!getIdentityMismatch()) return;
  try {
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
  } catch { /* show it */ }
  showIdentityRepairModal();
}

/** Profile → Security: re-check against the synced vault, then show the result. */
export async function checkIdentityNow() {
  try {
    if (getConnectionState() === "synced") await keypairManager?.initialize();
  } catch (err) {
    console.warn("[IdentityRepair] Re-check failed:", err);
  }
  showIdentityRepairModal();
}

export function showIdentityRepairModal() {
  const mismatch = getIdentityMismatch();
  document.getElementById("identity-repair-modal")?.remove();
  const modal = document.createElement("div");
  modal.className = "unlock-modal";
  modal.id = "identity-repair-modal";
  const text = (key, params) => escapeHtml(t(key, params));
  const code = (value) => `<code>${escapeHtml(value)}</code>`;
  modal.innerHTML = mismatch ? `
    <div class="unlock-modal-content">
      <button class="unlock-close-btn" data-action="later" title="${text("account.identity.later")}" aria-label="${text("account.identity.later")}">&times;</button>
      <h2>${text("account.identity.repairTitle")}</h2>
      <p>${fillHtml("account.identity.mismatch", {
        device: code(MapKeyTrust.fingerprint(mismatch.local)),
        published: code(MapKeyTrust.fingerprint(mismatch.published)),
      })}</p>
      <p>${text("account.identity.repairExplain")}</p>
      <div class="unlock-error hidden" data-ref="error"></div>
      <form class="unlock-form" data-ref="form">
        <input type="password" class="auth-input" data-ref="password" autocomplete="current-password"
               placeholder="${text("account.password.placeholder")}" required />
        <div style="display:flex; gap:8px;">
          <button type="button" class="auth-btn" data-action="later" style="background:#6b7280;">${text("account.identity.later")}</button>
          <button type="submit" class="auth-btn" style="flex:1;">${text("account.identity.repair")}</button>
        </div>
      </form>
    </div>` : `
    <div class="unlock-modal-content">
      <button class="unlock-close-btn" data-action="later" title="${text("common.close")}" aria-label="${text("common.close")}">&times;</button>
      <h2>${text("account.identity.title")}</h2>
      <p>${text("account.identity.upToDate")}</p>
      ${ownFingerprintHtml()}
      <div style="display:flex; gap:8px;">
        <button type="button" class="auth-btn" data-action="later" style="flex:1;">${text("common.close")}</button>
      </div>
    </div>`;
  document.body.appendChild(modal);

  const ref = (name) => modal.querySelector(`[data-ref="${name}"]`);
  const close = () => modal.remove();
  modal.querySelectorAll('[data-action="later"]').forEach((btn) => btn.addEventListener("click", () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    close();
  }));

  ref("form")?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const passwordInput = ref("password");
    const password = passwordInput.value;
    passwordInput.value = "";
    const errorEl = ref("error");
    const submit = e.target.querySelector('button[type="submit"]');
    submit.disabled = true;
    submit.textContent = t("account.identity.repairing");
    errorEl.classList.add("hidden");
    try {
      const result = await repairIdentity(password);
      if (result.alreadyMatches) {
        // Fixed from another device while this dialog was open (it only
        // opens for a mismatch confirmed on a synced vault).
        modal.querySelector(".unlock-modal-content").innerHTML = `
          <h2>${text("account.identity.alreadyTitle")}</h2>
          <p>${text("account.identity.alreadyText")}</p>
          <div style="display:flex; gap:8px;">
            <button type="button" class="auth-btn" data-action="done" style="flex:1;">${text("common.done")}</button>
          </div>`;
        modal.querySelector('[data-action="done"]').addEventListener("click", close);
        return;
      }
      modal.querySelector(".unlock-modal-content").innerHTML = `
        <h2>${text("account.identity.repairedTitle")}</h2>
        <p>${text("account.identity.repairedText")}</p>
        ${result.restoresRequested ? `<p>${text("account.identity.restoresPending", { count: result.restoresRequested })}</p>` : ""}
        <div style="display:flex; gap:8px;">
          <button type="button" class="auth-btn" data-action="done" style="flex:1;">${text("common.done")}</button>
        </div>`;
      modal.querySelector('[data-action="done"]').addEventListener("click", close);
    } catch (err) {
      errorEl.textContent = err?.message || t("account.identity.repairFailed");
      errorEl.classList.remove("hidden");
      submit.disabled = false;
      submit.textContent = t("account.identity.repair");
    }
  });
  ref("password")?.focus();
}

/**
 * The user's own key fingerprint, for others to compare out of band (the
 * same format the restore and invitation dialogs show them).
 */
function ownFingerprintHtml() {
  const publicKey = keypairManager?.keypair?.publicKey;
  if (!publicKey) return "";
  const fingerprint = MapKeyTrust.fingerprint(Keypair.toBase64(publicKey));
  return `<p>${fillHtml("account.identity.fingerprint", { fingerprint: `<code>${escapeHtml(fingerprint)}</code>` })}<br>
    <small>${escapeHtml(t("account.identity.fingerprintHint"))}</small></p>`;
}

/**
 * HTML of `key`'s text, escaped, with placeholders filled from `html`
 * (markup built by the caller, e.g. a fingerprint in <code>).
 */
function fillHtml(key, html) {
  const marks = Object.keys(html);
  const parts = t(key, Object.fromEntries(marks.map((name, i) => [name, `\u0000${i}\u0000`]))).split("\u0000");
  return parts.map((part, i) => (i % 2 ? html[marks[Number(part)]] : escapeHtml(part))).join("");
}

// ============================================
// Key decisions (A2, S-L1, S-L5; review 2026-09-24)
// ============================================

const fp = (key) => (key ? MapKeyTrust.fingerprint(key) : t("account.keyTrust.unknownKey"));

/**
 * Ask for a decision about keys, or about joining a map, that must not be a
 * plain OK: the fingerprints are shown and, when `checkbox` is given, the
 * confirm button stays disabled until the user ticks it.
 *
 * @param {Object} options
 * @param {string} options.title
 * @param {string[]} [options.paragraphs] - plain text
 * @param {Array<{label: string, name?: string, key?: string, note?: string}>} [options.rows]
 * @param {string[]} [options.warnings] - plain text, shown prominently
 * @param {string|null} [options.checkbox] - what the user must confirm
 * @param {string} options.confirmText
 * @param {string} [options.cancelText]
 * @returns {Promise<boolean>}
 */
export function confirmKeyDecision({ title, paragraphs = [], rows = [], warnings = [], checkbox = null, confirmText, cancelText = t("common.cancel") }) {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "unlock-modal key-decision-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const rowHtml = rows.map((row) => `
      <tr>
        <th style="text-align:left; padding:2px 8px 2px 0; font-weight:normal;">${escapeHtml(row.label)}</th>
        <td style="padding:2px 0;">${row.name ? `${escapeHtml(row.name)} ` : ""}${row.key ? `<code>${escapeHtml(fp(row.key))}</code>` : ""}${row.note ? ` <small>${escapeHtml(row.note)}</small>` : ""}</td>
      </tr>`).join("");
    modal.innerHTML = `
      <div class="unlock-modal-content">
        <button class="unlock-close-btn" data-action="cancel" title="${escapeHtml(cancelText)}" aria-label="${escapeHtml(cancelText)}">&times;</button>
        <h2>${escapeHtml(title)}</h2>
        ${warnings.map((w) => `<p class="unlock-error" role="alert">${escapeHtml(w)}</p>`).join("")}
        ${paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join("")}
        ${rows.length ? `<table style="margin:8px 0; border-collapse:collapse;">${rowHtml}</table>` : ""}
        ${checkbox ? `<label style="display:flex; gap:8px; align-items:flex-start; margin:12px 0;">
          <input type="checkbox" data-ref="verified" /> <span>${escapeHtml(checkbox)}</span></label>` : ""}
        <div style="display:flex; gap:8px;">
          <button type="button" class="auth-btn" data-action="cancel" style="background:#6b7280;">${escapeHtml(cancelText)}</button>
          <button type="button" class="auth-btn" data-action="confirm" style="flex:1;" ${checkbox ? "disabled" : ""}>${escapeHtml(confirmText)}</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
    const confirmBtn = modal.querySelector('[data-action="confirm"]');
    const box = modal.querySelector('[data-ref="verified"]');
    box?.addEventListener("change", () => { confirmBtn.disabled = !box.checked; });
    const finish = (result) => {
      document.removeEventListener("keydown", onKey, true);
      modal.remove();
      resolve(result);
    };
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); finish(false); }
    };
    document.addEventListener("keydown", onKey, true);
    modal.querySelectorAll('[data-action="cancel"]').forEach((btn) => btn.addEventListener("click", () => finish(false)));
    confirmBtn.addEventListener("click", () => {
      if (box && !box.checked) return;
      finish(true);
    });
    (box || confirmBtn).focus();
  });
}

/**
 * Owner: restore a member's access after their identity repair, only once
 * the owner confirmed the new key's fingerprint with the member out of band
 * (A2). The server alone asserts that the key changed and which key is new.
 * @returns {Promise<boolean>} false if the owner cancelled
 */
export async function confirmAndRestoreMemberAccess(docId, userId, username) {
  const info = await MapKeyTrust.describeMemberRestore(docId, userId, username);
  const name = info.username || username || t("account.identity.thisMember");
  const warnings = [];
  if (info.pin.status === "changed" && info.pin.pinnedKey !== info.previousKey && info.pin.pinnedKey !== info.newKey) {
    warnings.push(t("account.identity.otherPinWarning", { name, key: fp(info.pin.pinnedKey) }));
  } else if (info.pin.status === "conflict") {
    warnings.push(t("account.identity.pinConflictWarning", { name, keys: info.pin.seenKeys.map(fp).join(", ") }));
  }
  const verifiedBefore = info.pin.status === "match" && info.pin.pin?.verifiedAt && !info.unchanged;
  const ok = await confirmKeyDecision({
    title: t("account.identity.verifyTitle", { name }),
    paragraphs: [
      info.unchanged
        ? t("account.identity.restoreUnchanged", { name })
        : t("account.identity.restoreChanged", { name }),
      t("account.identity.restoreAsk", { name }),
      ...(verifiedBefore ? [t("account.identity.verifiedBefore", { name })] : []),
    ],
    rows: [
      { label: t("account.identity.rowTrusted"), key: info.previousKey },
      { label: t("account.identity.rowNewToVerify"), name, key: info.newKey },
    ],
    warnings,
    checkbox: t("account.identity.readMatch", { name, fingerprint: fp(info.newKey) }),
    confirmText: t("account.identity.restoreAccess"),
  });
  if (!ok) return false;
  await MapKeyTrust.restoreMemberAccess(docId, userId, username, { confirmedKey: info.newKey });
  return true;
}

/**
 * A collaborator's key differs from the one this vault pinned for them
 * (`identity_changed` from MapKeyTrust, or a pin warning on a map). Asks the
 * user to verify the new fingerprint out of band; on confirmation the new key
 * is pinned.
 * @param {{userId: string, username?: string, publicKey: string, pinnedKey?: string|null, seenKeys?: string[], status?: string}} identity
 * @param {{confirmText?: string, context?: string}} [options]
 * @returns {Promise<boolean>}
 */
export async function confirmChangedIdentity(identity, { confirmText = t("account.identity.trustNewKey"), context = "" } = {}) {
  const name = identity.username || t("account.keyTrust.thisUser");
  const known = identity.status === "conflict"
    ? (identity.seenKeys || []).filter((k) => k !== identity.publicKey)
    : [identity.pinnedKey].filter(Boolean);
  const ok = await confirmKeyDecision({
    title: t("account.identity.changedTitle", { name }),
    warnings: [t("account.identity.changedWarning", { name })],
    paragraphs: [
      ...(context ? [context] : []),
      t("account.identity.changedAsk", { name }),
    ],
    rows: [
      ...known.map((key) => ({ label: t("account.identity.rowSeenBefore"), key })),
      { label: t("account.identity.rowNew"), name, key: identity.publicKey },
    ],
    checkbox: t("account.identity.readMatch", { name, fingerprint: fp(identity.publicKey) }),
    confirmText,
  });
  if (ok) MapKeyTrust.confirmIdentity(identity.userId, identity.publicKey, identity.username);
  return ok;
}

/**
 * Before joining a map: who admitted the user, under which keys, and whether
 * anything disagrees with the vault's pins (S-L1) or can't be verified at
 * all (S-L5). Plain invitations from the owner whose key checks out don't
 * need this; callers use `enrollmentNeedsConfirmation`.
 * @param {{state: Object|null, owner: Object|null, inviter: Object|null, mismatches: Array}} details - MapKeyTrust.inspectEnrollment
 * @param {{title?: string, ownerUsername?: string, inviterUsername?: string, confirmText?: string}} [context]
 * @returns {Promise<boolean>}
 */
export function confirmEnrollment(details, { title = "", ownerUsername = "", inviterUsername = "", confirmText = t("account.identity.joinMap") } = {}) {
  // Sentences naming the map come in two forms: with its title in quotes, or "this map".
  const aboutMap = (key, params = {}) => t(`${key}${title ? "Named" : "This"}`, { ...params, title });
  const owner = details.owner;
  const inviter = details.inviter;
  const ownerKnown = owner?.username || ownerUsername || "";
  const ownerName = ownerKnown || t("account.identity.theOwner");
  const paragraphs = [];
  const warnings = [];
  const rows = [];
  if (!details.state) {
    warnings.push(aboutMap("account.identity.noHistory"));
    paragraphs.push(ownerKnown
      ? t("account.identity.joinOnlyIf", { owner: ownerKnown })
      : t("account.identity.joinOnlyIfNoOwner"));
  } else {
    rows.push({ label: t("account.identity.rowOwner"), name: ownerName, key: owner?.publicKey });
    if (inviter && owner && inviter.userId !== owner.userId) {
      const inviterName = inviter.username || inviterUsername || t("account.identity.anAdmin");
      paragraphs.push(aboutMap(inviter.role === "admin" ? "account.identity.invitedAdmin" : "account.identity.invitedMember",
        { inviter: inviterName, owner: ownerName }));
      rows.push({ label: t("account.identity.rowInvitedBy"), name: inviterName, key: inviter.publicKey, note: roleLabel(inviter.role) });
    }
  }
  for (const m of details.mismatches || []) {
    const name = m.username || t("account.identity.aMember");
    warnings.push(m.status === "conflict"
      ? t("account.identity.mismatchConflict", { name })
      : t("account.identity.mismatchChanged", { name, key: fp(m.pinnedKey) }));
  }
  if (warnings.length && details.state) {
    paragraphs.push(t("account.identity.askBeforeJoining"));
  }
  return confirmKeyDecision({
    title: aboutMap("account.identity.joinTitle"),
    paragraphs,
    rows,
    warnings,
    checkbox: (details.mismatches || []).length ? t("account.identity.comparedMatch") : null,
    confirmText,
  }).then((ok) => {
    if (ok) for (const m of details.mismatches || []) MapKeyTrust.confirmIdentity(m.userId, m.publicKey, m.username);
    return ok;
  });
}

/** A member's role as shown to the user (the stored value when unknown). */
function roleLabel(role) {
  return ["owner", "admin", "editor", "viewer"].includes(role) ? t(`account.identity.role.${role}`) : role;
}

/** Whether joining needs the dialog above (see confirmEnrollment). */
export function enrollmentNeedsConfirmation(details) {
  if (!details?.state) return true;
  return (details.mismatches || []).length > 0 ||
    !!(details.inviter && details.owner && details.inviter.userId !== details.owner.userId);
}
